// ---------- briques nuancées : la même brique, 256 couleurs, une vingtaine par tableau ----------
//   0          case vide, du papier nu (le fond détouré, les blancs purs)
//   1..23      les vingt-trois couleurs de `briques`, mêmes codes, mêmes noms
//   24..256    deux cent trente-trois couleurs de plus, réparties dans tout l'espace des couleurs
//
// D'OÙ VIENT CET ALPHABET (2026-10-18). Le principe de `peintureNuancee` appliqué à la mosaïque :
// la conversion CHOISIT les vingt couleurs du tableau dans un grand nuancier, au lieu d'en imposer
// vingt-trois posées à la main. Mesuré sur dix-neuf images (tableaux, monuments, photos ; 120
// cases, écart OKLab ×100 à l'original, rendu et original floutés d'une case, fond exclu) :
//
//   briques actuelles, 23 couleurs, image retouchée      9,84
//   les mêmes 23, sur l'image sans retouche              2,71
//   256 couleurs, 20 choisies par image, image brute     1,57   (meilleur sur 19 images sur 19)
//
// Le gros du gain vient de l'image brute, comme pour la peinture : les auto-niveaux assombrissent
// et faussent les couleurs. Le nuancier ajoute 42 % : les gris, les beiges et les tons chair que
// la palette fixe remplaçait par du bruit coloré. Effet de bord mesuré : +39 % de zones en moyenne
// à grille égale (de −23 % à +177 %) — l'atelier le lit dans son relevé, la grille se règle en
// conséquence. Planche : https://claude.ai/artifact/F2H93yA3a4zyvyFutFMKpV
//
// UN ALPHABET À PART ET NON `briques` ÉLARGI, pour la raison de `peintureNuancee` : un client qui
// ne connaît pas les codes au-delà de 23 recevrait des cases qu'il ne sait pas dessiner ; un
// identifiant neuf se déclare au serveur (knownAlphabetIds), qui ne l'envoie qu'aux versions qui
// savent le dessiner. Les grilles de `briques` restent intactes. Les badges le rangent dans la
// même famille (backend/src/utils/badges.ts) : une feuille finie ici compte pour « Briques ».
//
// Tout le dessin vient de `briques.ts` — la brique, ses tons, la mesure de ce qu'elle rend. Seuls
// changent le nuancier et la conversion (briquesNuancees.encode.ts).

import {
  BRICKS,
  brickTones,
  measureBricks,
  paintBrick,
  type BrickTones,
  type EffectiveBrick,
} from './briques';
import { hexRgb, srgbToOklab } from './peinture';
import { describeOklab } from './peintureNuancee';
import type { Alphabet } from './types';

/**
 * Les 233 couleurs ajoutées, codes 24 à 256, dans l'ordre où elles ont été tirées.
 *
 * Tirées une fois et ÉCRITES ici, pour la raison de peintureNuancee : un code garde son sens pour
 * toujours. La recette, pour mémoire : parmi les 4 096 couleurs d'une grille sRGB de 16 niveaux
 * par canal, prendre à chaque tour celle qui est la plus loin de toutes les couleurs déjà prises —
 * en partant des vingt-trois de `briques` —, avec la distance de l'encodeur (clarté², plus
 * 1,15 × l'écart de teinte²).
 */
const MORE: readonly string[] = [
  '#000000',
  '#00ffcc',
  '#ee00ff',
  '#00ff00',
  '#8866ff',
  '#0000ff',
  '#996600',
  '#ddff00',
  '#ff88ff',
  '#0000aa',
  '#6600ee',
  '#550055',
  '#000022',
  '#aa00ee',
  '#0088ff',
  '#00bb00',
  '#007788',
  '#ff8899',
  '#999944',
  '#55ccaa',
  '#995566',
  '#ccee99',
  '#6677bb',
  '#ffbbff',
  '#556600',
  '#110000',
  '#cc66ee',
  '#880066',
  '#77eeff',
  '#ff4444',
  '#220044',
  '#550000',
  '#3355ff',
  '#440099',
  '#ff00bb',
  '#cc7777',
  '#00dd77',
  '#bbaa88',
  '#003300',
  '#8800bb',
  '#000066',
  '#44aaff',
  '#aa66bb',
  '#bb0066',
  '#99ee55',
  '#cc22cc',
  '#3322cc',
  '#001100',
  '#ee0066',
  '#777766',
  '#553366',
  '#443300',
  '#993300',
  '#ccaa33',
  '#bbcc88',
  '#ff55ee',
  '#6655cc',
  '#8888ee',
  '#330011',
  '#00bb77',
  '#660088',
  '#442233',
  '#ffaa55',
  '#004455',
  '#007755',
  '#aaffdd',
  '#ffff88',
  '#dd5500',
  '#004488',
  '#ddaabb',
  '#9944cc',
  '#666688',
  '#aa0033',
  '#774466',
  '#77aa11',
  '#885599',
  '#8833ff',
  '#ee88cc',
  '#bb7733',
  '#007700',
  '#dd0099',
  '#7799cc',
  '#0077cc',
  '#00dd00',
  '#00ff88',
  '#aa7799',
  '#00ccdd',
  '#ccbbff',
  '#ddddff',
  '#ffdd88',
  '#99aabb',
  '#bb88ff',
  '#77aa88',
  '#775500',
  '#558899',
  '#556655',
  '#660033',
  '#778800',
  '#cc77bb',
  '#88dd88',
  '#6600bb',
  '#bb5588',
  '#001133',
  '#003333',
  '#ff55bb',
  '#99ddcc',
  '#993366',
  '#330066',
  '#220088',
  '#445588',
  '#dd5566',
  '#aa00bb',
  '#bb44ff',
  '#4422ff',
  '#ee9977',
  '#aa5533',
  '#440033',
  '#ffbbcc',
  '#998866',
  '#99aaff',
  '#ccdd44',
  '#99ff99',
  '#443388',
  '#448866',
  '#883388',
  '#ff6699',
  '#662200',
  '#cc55bb',
  '#ee1100',
  '#00aa44',
  '#99bb77',
  '#8866cc',
  '#6677ee',
  '#1133aa',
  '#0044dd',
  '#884444',
  '#006666',
  '#66cc33',
  '#5533cc',
  '#dd99ff',
  '#222200',
  '#ddddbb',
  '#6644ee',
  '#ffee22',
  '#005577',
  '#777733',
  '#ff7766',
  '#7733cc',
  '#669955',
  '#663344',
  '#005500',
  '#aaddff',
  '#006699',
  '#99ff00',
  '#cc3366',
  '#bb99aa',
  '#66bbbb',
  '#2288bb',
  '#220022',
  '#88eebb',
  '#887788',
  '#9988bb',
  '#bbeeee',
  '#cc8800',
  '#00bbee',
  '#33ddff',
  '#eeffcc',
  '#006633',
  '#ff3388',
  '#4466dd',
  '#ddbb66',
  '#990099',
  '#002244',
  '#445511',
  '#aa66ee',
  '#ee9900',
  '#33eedd',
  '#442255',
  '#331100',
  '#444433',
  '#55ee44',
  '#aaaa00',
  '#3399dd',
  '#880044',
  '#bb6655',
  '#333333',
  '#dd77dd',
  '#dd7799',
  '#66cc77',
  '#ee33dd',
  '#ffaa99',
  '#0000cc',
  '#665599',
  '#44ee99',
  '#332277',
  '#eeaadd',
  '#442211',
  '#bb9955',
  '#990000',
  '#99bbaa',
  '#ccff77',
  '#00bbaa',
  '#446677',
  '#4433aa',
  '#886644',
  '#cc00ee',
  '#002277',
  '#8833aa',
  '#bb0088',
  '#110011',
  '#bbbbdd',
  '#cc8866',
  '#004400',
  '#331144',
  '#334466',
  '#5566ff',
  '#66ffee',
  '#ffddcc',
  '#554400',
  '#665566',
  '#996699',
  '#ccbbaa',
  '#bb77dd',
  '#ee6644',
  '#aa6677',
  '#662255',
  '#bb0000',
  '#8800dd',
  '#888844',
  '#aa9988',
  '#775544',
  '#4466aa',
  '#005555',
];

/** Le nuancier complet, indice = code − 1. */
export const NUANCES: readonly string[] = [...BRICKS.map((b) => b.css), ...MORE];

/** Clarté et teinte OKLab de chaque couleur, indice = code − 1. Par peinture.ts et non par
 *  l'encodeur des briques : ce fichier est la face DESSIN, que le jeu embarque, et il ne doit rien
 *  importer de la conversion (cf. encoders.ts). */
const LAB = NUANCES.map((css) => srgbToOklab(...hexRgb(css)));

/** Les tons d'un code, calculés à la première rencontre : une feuille n'emploie qu'une vingtaine
 *  des 256, inutile de tout préparer. */
const tones = new Map<number, BrickTones>();
function tonesOf(code: number): BrickTones {
  let t = tones.get(code);
  if (!t) tones.set(code, (t = brickTones(NUANCES[code - 1])));
  return t;
}

let effective: { effective: EffectiveBrick[]; paperLum: number } | null = null;

/** Papier (code 0) puis les 256 briques telles qu'elles se voient — pour l'encodeur. Mesurées une
 *  fois, ~25 ms : pas de préchauffage nécessaire, à la différence de `peintureNuancee`. */
export function getEffectiveNuances(): { effective: EffectiveBrick[]; paperLum: number } {
  effective ??= measureBricks(NUANCES.length, (ctx, x, y, s, code) =>
    paintBrick(ctx, x, y, s, s, tonesOf(code)),
  );
  return effective;
}

/**
 * L'ORDRE DE LA PALETTE, la règle de `briques` étendue au nuancier : les couleurs vives d'abord,
 * dans l'ordre de l'arc-en-ciel à partir du rouge, puis les couleurs sans teinte (grises,
 * terreuses éteintes) ET LES SOMBRES, du clair au foncé — le NOIR EN DERNIER, pour que la feuille
 * ne s'ouvre pas sur le grand fond sombre. La palette d'une feuille n'en montre que la vingtaine
 * employée.
 *
 * « Sombre » quelle que soit la teinte : la première version ne regardait que la saturation, et
 * le chaton s'ouvrait sur un bordeaux presque noir — un « rouge » pour la règle, le fond du
 * tableau pour le joueur (vu dans le jeu le 2026-10-18).
 *
 * Calculé et non écrit : il ne fixe que l'ordre d'affichage, pas le sens d'un code, et reste donc
 * libre de changer sans toucher aux grilles publiées.
 */
const CHROMA_MIN = 0.04;
/** En dessous de cette clarté OKLab, une couleur se range avec les sombres, en fin de palette. */
const DARK_L = 0.45;
const RED_HUE = 20; // degrés OKLab : l'arc-en-ciel commence au rouge
const paletteOrder = (() => {
  const codes = NUANCES.map((_, i) => i + 1);
  const chroma = (c: number) => Math.hypot(LAB[c - 1][1], LAB[c - 1][2]);
  const hue = (c: number) =>
    ((Math.atan2(LAB[c - 1][2], LAB[c - 1][1]) * 180) / Math.PI - RED_HUE + 720) % 360;
  const isVivid = (c: number) => chroma(c) >= CHROMA_MIN && LAB[c - 1][0] >= DARK_L;
  const vivid = codes.filter(isVivid).sort((a, b) => hue(a) - hue(b));
  const plain = codes.filter((c) => !isVivid(c)).sort((a, b) => LAB[b - 1][0] - LAB[a - 1][0]);
  return [...vivid, ...plain];
})();

export const briquesNuancees: Alphabet = {
  id: 'briquesNuancees',
  sheetMode: 'single',
  bakesOnTop: true,
  label: 'Briques nuancées',
  symbolName: 'brique',
  selfColored: true,
  helpText:
    'Une mosaïque de briques à tenons, comme « Briques », mais les couleurs sont choisies pour ' +
    'chaque tableau parmi deux cent cinquante-six : une vingtaine, les plus justes.',

  aspect: () => 1,
  paletteOrder,
  // Pas de numéros, comme le point de croix et pour la même raison : un code est le rang d'une
  // couleur dans le nuancier de 256 (depuis le 2026-10-18), donc des cases marquées 143 ou 217 qui
  // ne renvoient à rien de lisible — la palette montre des couleurs. La surbrillance de la couleur
  // en cours guide le joueur (vu dans le jeu le 2026-10-30).
  bareSheet: true,
  // … mais la grille reste, comme au point de croix : c'est elle qui montre où sont les cases.
  ruledSheet: true,

  codeName: (code) => {
    if (code <= BRICKS.length) return BRICKS[code - 1]?.name ?? '';
    const lab = LAB[code - 1];
    return lab ? describeOklab(lab) : '';
  },

  draw(ctx, x, y, w, h, code) {
    if (!code || code > NUANCES.length) return;
    paintBrick(ctx, x, y, w, h, tonesOf(code));
  },
};
